import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaSignOutAlt } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import { findNearbyToilets, getMultipleToiletOpeningHours } from '../../utils/toiletService';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../context/AuthContext';
import './RouteResults.css';

const interpolateAlongPath = (path, fraction) => {
  if (!path || path.length === 0 || !window.google || !window.google.maps.geometry) return null;
  if (path.length === 1) return path[0];

  const totalDistance = window.google.maps.geometry.spherical.computeLength(path);
  if (totalDistance === 0) return path[0];

  const targetDistance = fraction * totalDistance;
  let distanceCovered = 0;

  for (let i = 0; i < path.length - 1; i++) {
    const startPoint = path[i];
    const endPoint = path[i + 1];
    const segmentDistance = window.google.maps.geometry.spherical.computeDistanceBetween(startPoint, endPoint);

    if (distanceCovered + segmentDistance >= targetDistance) {
      const distanceIntoSegment = targetDistance - distanceCovered;
      const segmentFraction = segmentDistance > 0 ? distanceIntoSegment / segmentDistance : 0;
      return window.google.maps.geometry.spherical.interpolate(startPoint, endPoint, segmentFraction);
    }
    distanceCovered += segmentDistance;
  }
  return path[path.length - 1];
};

const getGoogleTravelMode = (transport) => {
  switch(transport) {
    case 'public':
      return 'TRANSIT';
    case 'cycling':
      return 'BICYCLING';
    case 'walking':
      return 'WALKING';
    case 'car':
    default:
      return 'DRIVING';
  }
};

  // Function to check if toilet is open at journey date/time
  const checkIfToiletIsOpen = (hours, journeyDate, journeyTime) => {
    if (!hours || hours.length === 0) {
      return { isOpen: true, reason: 'Opening hours not available' }; // Include if no data
    }

    // Parse journey date and time
    const journeyDateTime = new Date(`${journeyDate}T${journeyTime}`);
    const dayOfWeek = journeyDateTime.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Convert to database format (0 = Monday, 6 = Sunday)
    const dbDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    // Find opening hours for this day
    const dayHours = hours.find(hour => hour.dow === dbDayOfWeek);
    
    if (!dayHours) {
      return { isOpen: false, reason: 'Closed on this day' };
    }

    // Check for 24-hour opening (00:00 - 00:00)
    if (dayHours.open_time === '00:00' && dayHours.close_time === '00:00') {
      return {
        isOpen: true,
        reason: 'Open 24 hours',
        openTime: '00:00',
        closeTime: '00:00'
      };
    }

    // Parse opening and closing times
    const [openHour, openMinute] = dayHours.open_time.split(':').map(Number);
    const [closeHour, closeMinute] = dayHours.close_time.split(':').map(Number);
    
    const openTime = new Date(journeyDate);
    openTime.setHours(openHour, openMinute, 0);
    
    const closeTime = new Date(journeyDate);
    closeTime.setHours(closeHour, closeMinute, 0);
    
    const isOpen = journeyDateTime >= openTime && journeyDateTime <= closeTime;
    
    return {
      isOpen,
      reason: isOpen ? 'Open' : 'Closed',
      openTime: dayHours.open_time,
      closeTime: dayHours.close_time
    };
  };

  const getHaversineDistance = (point1, point2) => {
  const R = 6371e3; // metres
  const φ1 = (point1.lat * Math.PI) / 180; // φ, λ in radians
  const φ2 = (point2.lat * Math.PI) / 180;
  const Δφ = ((point2.lat - point1.lat) * Math.PI) / 180;
  const Δλ = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // in metres
};

const RouteResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { 
    startLocation, 
    endLocation, 
    startCoordinates,
    endCoordinates,
    disabilityAccess, 
    frequency, 
    tolerance, 
    selectedTransport 
  } = location.state || {};

  const mapRef = useRef(null);
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState(null);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(null);
  const [placesByRoute, setPlacesByRoute] = useState({});
  const [selectedToilets, setSelectedToilets] = useState({});
  const [openingHours, setOpeningHours] = useState({});
  const [collapsedRoutes, setCollapsedRoutes] = useState({ 0: true, 1: true, 2: true, 3: true, 4: true, mobile: true });
  const startEndMarkers = useRef([]);
  const transportChangeMarkers = useRef([]);
  // Reintroduced refs/states required across the module
  const mapInstance = useRef(null);
  const placesService = useRef(null);
  const placeMarkers = useRef([]);
  const [databaseLoading, setDatabaseLoading] = useState({});
  const [routeJsons, setRouteJsons] = useState([]);
  const [aiLoading, setAiLoading] = useState({});
  const [aiRecommendation, setAiRecommendation] = useState({});
  const [markersRendered, setMarkersRendered] = useState(false);
  const polylines = useRef([]);
  const allRoutesBoundsRef = useRef(null);
  const hasFittedAllBoundsRef = useRef(false);
  const [toiletsAvailable, setToiletsAvailable] = useState({});

  // Removed states; provide no-op setters for code paths that still call them
  const setOriginalRoutes = () => {};
  const setDatabaseToilets = () => {};
  const setCombinedToilets = () => {};

  // Redirect to home page if no route data (page refresh)
  useEffect(() => {
    // Only redirect after Google Maps is initialized and if no route data exists
    if (mapInstance.current && (!startLocation || !endLocation)) {
      navigate('/');
      return;
    }
  }, [startLocation, endLocation, navigate]);


  const getTransitModes = (route) => {
    if (selectedTransport !== 'public' || !route) return null;

    const modes = new Set();
    route.legs.forEach(leg => {
      leg.steps.forEach(step => {
        if (step.travel_mode === 'WALKING') {
          modes.add('Walk');
        } else if (step.travel_mode === 'TRANSIT' && step.transit) {
          const vehicleType = step.transit.line.vehicle.type;
          const formattedType = vehicleType.charAt(0).toUpperCase() + vehicleType.slice(1).toLowerCase().replace('_', ' ');
          modes.add(formattedType);
        }
      });
    });
    return Array.from(modes).join(', ');
  };

  const getTransportIcon = (transportMode) => {
    switch (transportMode?.toLowerCase()) {
      case 'walking':
        return '🚶';
      case 'cycling':
        return '🚴';
      case 'driving':
        return '🚗';
      case 'public':
        return '🚌';
      case 'transit':
        return '🚇';
      default:
        return '🚗';
    }
  };

  const getTransportDisplayText = (transportMode) => {
    if (!transportMode) return 'Not specified';
    
    switch (transportMode.toLowerCase()) {
      case 'walking':
        return 'Walking';
      case 'cycling':
        return 'Cycling';
      case 'driving':
        return 'Driving';
      case 'public':
        return 'Public Transport';
      case 'transit':
        return 'Transit';
      default:
        return transportMode.charAt(0).toUpperCase() + transportMode.slice(1);
    }
  };

  const addStartEndMarkers = useCallback(async () => {
    if (!mapInstance.current || !startLocation || !endLocation) return;

    // Clear existing start/end markers
    startEndMarkers.current.forEach(marker => marker.setMap(null));
    startEndMarkers.current = [];

    try {
      // Create start marker (green circle with "S")
      const startMarker = new window.google.maps.Marker({
        position: startCoordinates,
        map: mapInstance.current,
        title: `Start: ${startLocation}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#4CAF50',
          fillOpacity: 1,
          strokeColor: '#2E7D32',
          strokeWeight: 2,
        },
        label: {
          text: 'S',
          color: '#FFFFFF',
          fontSize: '12px',
          fontWeight: 'bold'
        }
      });
      startEndMarkers.current.push(startMarker);

      // Create end marker (red circle with "E")
      const endMarker = new window.google.maps.Marker({
        position: endCoordinates,
        map: mapInstance.current,
        title: `End: ${endLocation}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#F44336',
          fillOpacity: 1,
          strokeColor: '#C62828',
          strokeWeight: 2,
        },
        label: {
          text: 'E',
          color: '#FFFFFF',
          fontSize: '12px',
          fontWeight: 'bold'
        }
      });
      startEndMarkers.current.push(endMarker);

      // Markers have been rendered
      setMarkersRendered(true);

    } catch (error) {
      setTimeout(() => { throw error; }, 0);
    }
  }, [startLocation, endLocation, startCoordinates, endCoordinates]);

  const addTransportChangeMarkers = useCallback((route) => {
    if (!mapInstance.current || !route || selectedTransport !== 'public') return;

    // Clear existing transport change markers
    transportChangeMarkers.current.forEach(marker => marker.setMap(null));
    transportChangeMarkers.current = [];

    try {
      let markerIndex = 1;
      
      route.legs.forEach((leg, legIndex) => {
        leg.steps.forEach((step, stepIndex) => {
          // Only add markers for transit steps (not walking)
          if (step.travel_mode === 'TRANSIT' && step.transit) {
            const transitInfo = step.transit;
            const vehicleType = transitInfo.line?.vehicle?.type || 'transit';
            const lineName = transitInfo.line?.short_name || transitInfo.line?.name || 'Transit';
            
            // Get the start location of this transit step
            const position = step.start_location;
            
            // Get transport icon based on vehicle type
            const getTransportIcon = (type) => {
              switch (type.toLowerCase()) {
                case 'bus':
                  return '🚌';
                case 'train':
                  return '🚆';
                case 'subway':
                case 'metro':
                  return '🚇';
                case 'tram':
                case 'light_rail':
                  return '🚊';
                case 'ferry':
                  return '⛴️';
                case 'cable_car':
                  return '🚡';
                case 'gondola':
                  return '🚠';
                default:
                  return '🚌'; // Default to bus
              }
            };
            
            const transportIcon = getTransportIcon(vehicleType);
            
            // Create transport change marker with icon
            const marker = new window.google.maps.Marker({
              position: position,
              map: mapInstance.current,
              title: `Transport Change ${markerIndex}: ${lineName} (${vehicleType})`,
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 16,
                fillColor: '#FFFFFF',
                fillOpacity: 1,
                strokeColor: '#FF9800',
                strokeWeight: 3,
              },
              label: {
                text: transportIcon,
                color: '#000000',
                fontSize: '14px',
                fontWeight: 'bold'
              }
            });

            // Add click listener to show transport details
            marker.addListener('click', () => {
              const infoWindow = new window.google.maps.InfoWindow({
                content: `
                  <div style="padding: 8px; font-family: Arial, sans-serif;">
                    <h3 style="margin: 0 0 8px 0; color: #004C10;">Transport Change ${markerIndex}</h3>
                    <p style="margin: 4px 0;"><strong>Line:</strong> ${lineName}</p>
                    <p style="margin: 4px 0;"><strong>Type:</strong> ${vehicleType.replace('_', ' ')}</p>
                    <p style="margin: 4px 0;"><strong>From:</strong> ${transitInfo.departure_stop?.name || 'Unknown'}</p>
                    <p style="margin: 4px 0;"><strong>To:</strong> ${transitInfo.arrival_stop?.name || 'Unknown'}</p>
                    <p style="margin: 4px 0;"><strong>Duration:</strong> ${Math.round(step.duration.value / 60)} min</p>
                  </div>
                `
              });
              infoWindow.open(mapInstance.current, marker);
            });

            transportChangeMarkers.current.push(marker);
            markerIndex++;
          }
        });
      });

    } catch (error) {
      setTimeout(() => { throw error; }, 0);
    }
  }, [selectedTransport]);

  const getTotalDistanceText = (route) => {
    const totalMeters = route.legs.reduce((sum, leg) => sum + leg.distance.value, 0);
    return `${(totalMeters / 1000).toFixed(1)} km`;
  };
  
  const getTotalDurationText = (route) => {
    const totalSeconds = route.legs.reduce((sum, leg) => sum + leg.duration.value, 0);
    if (totalSeconds < 60) return "< 1 min";
  
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
  
    const parts = [];
    if (hours > 0) parts.push(`${hours} hr`);
    if (minutes > 0) parts.push(`${minutes} min`);
    
    return parts.join(' ');
  };

  const getTotalDurationSeconds = (route) => {
    return route.legs.reduce((acc, leg) => acc + (leg.duration.value || 0), 0);
  };

  const secondsToMinutes = (seconds) => Math.round(seconds / 60);
  const calculateFrequencyInterval = (timeElapsedSeconds, frequency) => {
    return frequency > 0 ? Math.floor((timeElapsedSeconds / 60) / frequency) : 0;
  };

  const extractToiletLocation = (place) => {
    if (!place.geometry || !place.geometry.location) return null;
    
    return {
      lat: typeof place.geometry.location.lat === 'function' 
        ? place.geometry.location.lat() 
        : place.geometry.location.lat,
      lng: typeof place.geometry.location.lng === 'function' 
        ? place.geometry.location.lng() 
        : place.geometry.location.lng
    };
  };

  const calculateDistanceFromRoute = (minDistance) => {
    return minDistance === Infinity ? null : Math.round(minDistance);
  };

  // Calculate toilet distance and timing data from route
  const calculateToiletRouteData = useCallback((place, route) => {
    const toiletLocation = extractToiletLocation(place);
    let minDistance = Infinity;
    let timeElapsed = 0;

    if (toiletLocation && route.legs) {
      let cumulativeTime = 0;
      for (const leg of route.legs) {
        for (const step of leg.steps) {
          for (const point of step.path) {
            const routePoint = { lat: point.lat(), lng: point.lng() };
            const distance = getHaversineDistance(toiletLocation, routePoint);
            if (distance < minDistance) {
              minDistance = distance;
              timeElapsed = cumulativeTime;
            }
          }
          cumulativeTime += step.duration.value;
        }
      }
    }
    
    const totalDuration = getTotalDurationSeconds(route);
    const timeRemaining = totalDuration - timeElapsed;

    return {
      distanceFromRoute: calculateDistanceFromRoute(minDistance),
      timeElapsed: secondsToMinutes(timeElapsed),
      timeRemaining: secondsToMinutes(timeRemaining),
      frequencyInterval: calculateFrequencyInterval(timeElapsed, frequency)
    };
  }, [frequency]);

  const getMaxDeviationMeters = (tolerance) => Math.round(1000 * (tolerance / 100));

  // Reusable function to process toilet data with route information
  const processToiletData = useCallback((places, route) => {
    return places.map(place => {
      const toiletLocation = extractToiletLocation(place);
      const routeData = calculateToiletRouteData(place, route);
      const stablePlaceId = place.place_id || (place.id ? `db_${place.id}` : undefined);
      return { 
        ...place, 
        place_id: stablePlaceId, 
        name: place.name, 
        address: place.address || place.vicinity || place.formatted_address, 
        location: toiletLocation, 
        ...routeData 
      };
    });
  }, [calculateToiletRouteData]);

  // Reusable function to filter toilets based on distance and opening hours
  const filterToilets = useCallback((toiletsWithData, maxDeviationMeters, openingHoursData) => {
    return toiletsWithData.filter(toilet => {
      // Filter out toilets outside tolerance
      if (toilet.distanceFromRoute === null || toilet.distanceFromRoute > maxDeviationMeters) {
        return false;
      }
      
      // Filter database toilets by opening hours
      if (toilet.place_id && toilet.place_id.startsWith('db_')) {
        if (openingHoursData[toilet.id]) {
          const { journeyDate, journeyTime } = location.state || {};
          const openStatus = checkIfToiletIsOpen(openingHoursData[toilet.id], journeyDate, journeyTime);
          if (!openStatus.isOpen) return false;
        }
      }
      
      return true;
    });
  }, [location.state]);

  // Reusable function to sort final candidates by distance
  const sortCandidatesByDistance = useCallback((candidates) => {
    return candidates.sort((a, b) => {
      if (a.distanceFromRoute === null && b.distanceFromRoute === null) return 0;
      if (a.distanceFromRoute === null) return 1;
      if (b.distanceFromRoute === null) return -1;
      return a.distanceFromRoute - b.distanceFromRoute;
    });
  }, []);

  // Reusable function to track frequency interval availability
  const trackFrequencyIntervals = useCallback((sortedCandidates, maximum_Number) => {
    const frequencyIntervals = {};
    
    for (let i = 0; i <= maximum_Number - 1; i++) {
      const hasToiletInInterval = sortedCandidates.some(toilet => {
        return toilet.frequencyInterval === i;
      });
      frequencyIntervals[i] = hasToiletInInterval;
    }
    
    return frequencyIntervals;
  }, []);

  // Reusable function to process toilet scoring and selection for a route
  const processToiletSelection = useCallback((filteredToilets, maximum_Number, totalDurationMinutes, maxDeviationMeters) => {
    const stopInterval = maximum_Number > 0 ? totalDurationMinutes / (maximum_Number + 1) : totalDurationMinutes;
    
    // Score toilets based on distance and timing
    const scoredToilets = filteredToilets.map(toilet => {
      let minTimeDiff = Infinity;
      if (maximum_Number > 0) {
        for (let i = 1; i <= maximum_Number; i++) {
          const idealTime = stopInterval * i;
          const timeDiff = Math.abs(toilet.timeElapsed - idealTime);
          if (timeDiff < minTimeDiff) {
            minTimeDiff = timeDiff;
          }
        }
      } else {
        minTimeDiff = 0;
      }
      
      const normalizedDistance = toilet.distanceFromRoute / maxDeviationMeters;
      const normalizedTimeDiff = totalDurationMinutes > 0 ? minTimeDiff / totalDurationMinutes : 0;
      const score = (0.5 * normalizedDistance) + (0.5 * normalizedTimeDiff);
      return { ...toilet, score };
    });

    // Ensure at least one toilet per frequency interval is included
    const topCandidates = [];
    const maxCandidates = 20;
    
    if (maximum_Number > 0) {
      // Group toilets by frequency interval
      const toiletsByInterval = {};
      scoredToilets.forEach(toilet => {
        const interval = toilet.frequencyInterval;
        if (!toiletsByInterval[interval]) toiletsByInterval[interval] = [];
        toiletsByInterval[interval].push(toilet);
      });
      
      // First, ensure at least one toilet from each interval
      Object.keys(toiletsByInterval).forEach(interval => {
        const intervalToilets = toiletsByInterval[interval].sort((a, b) => a.score - b.score);
        if (intervalToilets.length > 0 && topCandidates.length < maxCandidates) {
          topCandidates.push(intervalToilets[0]);
        }
      });
      
      // Then fill remaining slots with best overall candidates
      const remainingSlots = maxCandidates - topCandidates.length;
      if (remainingSlots > 0) {
        const remainingCandidates = scoredToilets
          .filter(toilet => !topCandidates.includes(toilet))
          .sort((a, b) => a.score - b.score)
          .slice(0, remainingSlots);
        topCandidates.push(...remainingCandidates);
      }
    } else {
      // If no frequency intervals, just take top 20 by score
      topCandidates.push(...scoredToilets.sort((a, b) => a.score - b.score).slice(0, maxCandidates));
    }

    return topCandidates;
  }, []);

  // Check if any toilets are available for a route
  const hasToiletsForRoute = (routeIndex) => {
    const places = placesByRoute[routeIndex] || [];
    return places.length > 0;
  };

  // Define Places search BEFORE it's used by searchToiletsForRoute to avoid TDZ runtime errors
  const findPlacesAlongRoute = useCallback((route, tolerance) => {
    return new Promise((resolve, reject) => {
      const service = placesService.current;
      if (!service) {
        setTimeout(() => { throw new Error('Places service is not initialized.'); }, 0);
        return reject(new Error('Places service is not initialized.'));
      }
      if (!route || !route.legs || route.legs.length === 0) {
        return reject(new Error('Invalid route data for place search.'));
      }

      // Updated interval logic: frequency slider value is minutes between stops
      const minInterval = 15;
      const intervalMinutes = Math.max(minInterval, Number(frequency));
      const searchIntervalSeconds = intervalMinutes * 60;

      // Search radius logic: positively correlated with tolerance
      // 0% tolerance = 0m, 50% = 500m, 100% = 1000m, 250% = 2500m
      const toleranceFraction = Math.max(0, Math.min(1, (typeof tolerance !== 'undefined' ? tolerance : 0) / 100));
      let searchRadius = 1000 * toleranceFraction; // in meters
      
      // Limit search radius to 1000m for non-driving/public transport modes
      if (selectedTransport !== 'driving' && selectedTransport !== 'public') {
        searchRadius = Math.min(searchRadius, 1000);
      }

      const searchPoints = [];
      let durationAccumulator = 0;
      
      for (const leg of route.legs) {
        const sumOfStepDurations = leg.steps.reduce((sum, step) => sum + step.duration.value, 0);
        const legDuration = leg.duration.value;
        const scalingFactor = (sumOfStepDurations > 0 && legDuration > sumOfStepDurations) ? legDuration / sumOfStepDurations : 1.0;

        for (const step of leg.steps) {
          const scaledStepDuration = step.duration.value * scalingFactor;
          const stepEndDuration = durationAccumulator + scaledStepDuration;

          let nextSearchDuration = (Math.floor(durationAccumulator / searchIntervalSeconds) + 1) * searchIntervalSeconds;
          if(nextSearchDuration <= durationAccumulator) {
            nextSearchDuration += searchIntervalSeconds;
          }

          while (nextSearchDuration < stepEndDuration) {
            const timeIntoStep = nextSearchDuration - durationAccumulator;
            const fraction = scaledStepDuration > 0 ? timeIntoStep / scaledStepDuration : 0;
            
            const searchPoint = interpolateAlongPath(step.path, fraction) || step.start_location;
            searchPoints.push(searchPoint);

            nextSearchDuration += searchIntervalSeconds;
          }
          durationAccumulator = stepEndDuration;
        }
      }
      

      // Cap search points at 5 to prevent excessive API calls
      const MAX_SEARCH_POINTS = 5;
      const limitedSearchPoints = searchPoints.slice(0, MAX_SEARCH_POINTS);
      
      const uniquePlaces = new Map();
      const searchPromises = limitedSearchPoints.map(point => {
        return new Promise(innerResolve => {
          const request = {
            location: point,
            radius: searchRadius,
            keyword: 'toilet restroom wc',
          };
          service.nearbySearch(request, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
              results.forEach(place => {
                // Exclude places with 'ltd' in the name (case-insensitive)
                if (!/ltd/i.test(place.name)) {
                  if (!uniquePlaces.has(place.place_id)) {
                    uniquePlaces.set(place.place_id, place);
                  }
                }
              });
            } else if (status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS && 
                       status !== window.google.maps.places.PlacesServiceStatus.OK) {
              setTimeout(() => { throw new Error(`Places API search at ${point.toString()} failed with status: ${status}`); }, 0);
            }
            innerResolve();
          });
        });
      });

      Promise.all(searchPromises).then(() => {
        resolve(Array.from(uniquePlaces.values()));
      });
    });
  }, [placesService, frequency, selectedTransport]);

  // Wrapper declared before usage to avoid TDZ; implementation is hoisted function below
  const findDatabaseToiletsAlongRoute = useCallback(async (route, tolerance) => {
    try {
      const searchPoints = [];
      const minInterval = 15;
      const intervalMinutes = Math.max(minInterval, Number(frequency));
      const searchIntervalSeconds = intervalMinutes * 60;
      const toleranceFraction = Math.max(0, Math.min(1, (typeof tolerance !== 'undefined' ? tolerance : 0) / 100));
      let searchRadius = 1000 * toleranceFraction; // meters
      if (selectedTransport !== 'driving' && selectedTransport !== 'public') {
        searchRadius = Math.min(searchRadius, 1000);
      }

      let durationAccumulator = 0;
      for (const leg of route.legs) {
        const sumOfStepDurations = leg.steps.reduce((sum, step) => sum + step.duration.value, 0);
        const legDuration = leg.duration.value;
        const scalingFactor = (sumOfStepDurations > 0 && legDuration > sumOfStepDurations) ? legDuration / sumOfStepDurations : 1.0;
        for (const step of leg.steps) {
          const scaledStepDuration = step.duration.value * scalingFactor;
          const stepEndDuration = durationAccumulator + scaledStepDuration;
          let nextSearchDuration = (Math.floor(durationAccumulator / searchIntervalSeconds) + 1) * searchIntervalSeconds;
          if (nextSearchDuration <= durationAccumulator) nextSearchDuration += searchIntervalSeconds;
          while (nextSearchDuration < stepEndDuration) {
            const timeIntoStep = nextSearchDuration - durationAccumulator;
            const fraction = scaledStepDuration > 0 ? timeIntoStep / scaledStepDuration : 0;
            const searchPoint = interpolateAlongPath(step.path, fraction) || step.start_location;
            searchPoints.push(searchPoint);
            nextSearchDuration += searchIntervalSeconds;
          }
          durationAccumulator = stepEndDuration;
        }
      }
      
      const MAX_SEARCH_POINTS = 5;
      const limitedSearchPoints = searchPoints.slice(0, MAX_SEARCH_POINTS);
      const allDatabaseToilets = new Map();
      for (const searchPoint of limitedSearchPoints) {
        try {
          const toilets = await findNearbyToilets(
            searchPoint.lat(),
            searchPoint.lng(),
            searchRadius / 1000,
            10
          );
          if (toilets && Array.isArray(toilets)) {
            const toiletPromises = toilets.map(async (toilet) => {
              if (!allDatabaseToilets.has(toilet.primary_id)) {
                let minDistance = Infinity;
                let timeElapsed = 0;
                if (route.legs) {
                  let cumulativeTime = 0;
                  for (const leg of route.legs) {
                    for (const step of leg.steps) {
                      for (const point of step.path) {
                        const routePoint = { lat: point.lat(), lng: point.lng() };
                        const toiletPoint = { lat: parseFloat(toilet.longitude), lng: parseFloat(toilet.latitude) };
                        const distance = getHaversineDistance(toiletPoint, routePoint);
                        if (distance < minDistance) { minDistance = distance; timeElapsed = cumulativeTime; }
                      }
                      cumulativeTime += step.duration.value;
                    }
                  }
                }
                const totalDuration = getTotalDurationSeconds(route);
                const timeRemaining = totalDuration - timeElapsed;
                const parsedLat = parseFloat(toilet.latitude);
                const parsedLng = parseFloat(toilet.longitude);
                let resolvedAddress = toilet.areas?.address || 'Address not available';
                if (toilet.address && toilet.address !== 'Address not available') {
                  resolvedAddress = toilet.address;
                }
                const enhancedToilet = {
                  ...toilet,
                  distanceFromRoute: calculateDistanceFromRoute(minDistance),
                  timeElapsed: secondsToMinutes(timeElapsed),
                  timeRemaining: secondsToMinutes(timeRemaining),
                  frequencyInterval: calculateFrequencyInterval(timeElapsed, frequency),
                  place_id: `db_${toilet.primary_id}`,
                  id: toilet.id,
                  name: toilet.name,
                  address: resolvedAddress,
                  geometry: { location: { lat: parsedLng, lng: parsedLat } },
                  vicinity: resolvedAddress,
                  is_men_only: Boolean(toilet.is_men_only),
                  is_women_only: Boolean(toilet.is_women_only),
                  is_gender_neutral: Boolean(toilet.is_gender_neutral)
                };
                allDatabaseToilets.set(toilet.primary_id, enhancedToilet);
              }
            });
            await Promise.all(toiletPromises);
          }
        } catch (error) {
          setTimeout(() => { throw error; }, 0);
        }
      }
      return Array.from(allDatabaseToilets.values());
    } catch (error) {
      setTimeout(() => { throw error; }, 0);
      return [];
    }
  }, [frequency, selectedTransport]);

  const searchToiletsForRoute = useCallback(async (route, idx) => {
    setDatabaseLoading(prev => ({ ...prev, [idx]: true }));
    
    try {
      // Fetch Google Places first (more reliable)
      const googlePlaces = await findPlacesAlongRoute(route, tolerance);
      
      // Try to fetch database toilets, but don't fail if it doesn't work
      let databaseToilets = [];
      try {
        databaseToilets = await findDatabaseToiletsAlongRoute(route, tolerance);
      } catch (dbError) {
        setTimeout(() => { throw new Error(`Database toilet search failed, continuing with Google Places only: ${dbError.message}`); }, 0);
        setDatabaseToilets(prev => ({ ...prev, [idx]: [] }));
      }

      // Calculate distance and timing data for Google Places toilets BEFORE deduplication
      const processedGooglePlaces = googlePlaces.map(place => {
        const toiletLocation = extractToiletLocation(place);
        const routeData = calculateToiletRouteData(place, route);

        return {
          ...place,
          place_id: place.place_id,
          name: place.name,
          address: place.vicinity || place.formatted_address,
          location: toiletLocation,
          ...routeData
        };
      });

      // Combine and deduplicate toilets (now with calculated data)
      // Prioritize database toilets over Google Places results
      const allToilets = [...processedGooglePlaces, ...databaseToilets];
      const uniqueToilets = new Map();
      
      allToilets.forEach(toilet => {
        const address = toilet.address || toilet.vicinity || toilet.formatted_address || 'Unknown';
        const key = address.toLowerCase().trim();
        
        // Check if this is a database toilet
        const isDatabaseToilet = toilet.place_id && toilet.place_id.startsWith('db_');
        
        if (!uniqueToilets.has(key)) {
          // No existing toilet with this address, add it
          uniqueToilets.set(key, toilet);
        } else {
          // Address already exists, check if we should replace it
          const existingToilet = uniqueToilets.get(key);
          const existingIsDatabase = existingToilet.place_id && existingToilet.place_id.startsWith('db_');
          
          // Replace Google Places toilet with database toilet (database toilets have priority)
          if (!existingIsDatabase && isDatabaseToilet) {
            uniqueToilets.set(key, toilet);
          }
          // If both are database toilets or both are Google Places, keep the first one
        }
      });

      const combinedToilets = Array.from(uniqueToilets.values());
      
      // If accessibility is required, only keep database toilets explicitly marked accessible
      const finalToilets = disabilityAccess
        ? combinedToilets.filter(toilet => {
            const isDatabaseToilet = toilet.place_id && toilet.place_id.startsWith('db_');
            return isDatabaseToilet && toilet.is_accessible === true;
          })
        : combinedToilets;

      // Store toilets for this route (already filtered when accessibility is required)
      setCombinedToilets(prev => ({ ...prev, [idx]: finalToilets }));

              // Fetch opening hours for database toilets
        let openingHoursData = {};
        if (databaseToilets.length > 0) {
          try {
            const toiletIds = databaseToilets.map(toilet => toilet.id);
            openingHoursData = await getMultipleToiletOpeningHours(toiletIds);
          } catch (error) {
            setTimeout(() => { throw error; }, 0);
          }
        }

      return { idx, places: finalToilets, route, googlePlaces, databaseToilets, openingHoursData };
    } catch (error) {
      setTimeout(() => { throw new Error(`Toilet search error: ${error.message || error}`); }, 0);
      const googlePlaces = await findPlacesAlongRoute(route, tolerance);
      return { idx, places: googlePlaces, route, googlePlaces, databaseToilets: [] };
    } finally {
      setDatabaseLoading(prev => ({ ...prev, [idx]: false }));
    }
  }, [tolerance, findPlacesAlongRoute, findDatabaseToiletsAlongRoute, calculateToiletRouteData, disabilityAccess]);

  const toggleToiletSelection = (placeId) => {
    setSelectedToilets(prev => ({
      ...prev,
      [placeId]: !prev[placeId],
    }));
  };

  const toggleRouteCollapse = (routeIndex) => {
    setCollapsedRoutes(prev => ({
      ...prev,
      [routeIndex]: !prev[routeIndex]
    }));
  };

  const toggleMobilePanel = () => {
    setCollapsedRoutes(prev => ({
      ...prev,
      mobile: !prev.mobile
    }));
  };  

  //Map Initialisation
  useEffect(() => {
    if (!window.google || !window.google.maps || mapInstance.current) return;
    
    try {
    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 0, lng: 0 },
      zoom: 10,
    });
    placesService.current = new window.google.maps.places.PlacesService(mapInstance.current);
    } catch (error) {
      setTimeout(() => { throw new Error(`Failed to initialize Google Maps: ${error.message}`); }, 0);
    }
  }, []);

  const currentPlaces = React.useMemo(
    () => (placesByRoute[selectedRouteIndex] || []),
    [placesByRoute, selectedRouteIndex]
  );

  

  useEffect(() => {
    if (!startLocation || !endLocation || !placesService.current) {
      return;
    }
    
    const travelMode = getGoogleTravelMode(selectedTransport);
    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: startLocation,
        destination: endLocation,
        travelMode: window.google.maps.TravelMode[travelMode],
        provideRouteAlternatives: true,
      },
      (response, status) => {
        if (status === 'OK') {
          const limitedRoutes = response.routes.slice(0, 3);
          setRoutes(limitedRoutes);
          setOriginalRoutes(limitedRoutes);
          setSelectedRouteIndex(0);
          setError(null);

          // Search toilets for all available routes (0..2) on load
          searchToiletsForRoute(limitedRoutes[0], 0).then(result => {
            const { idx, places, route, openingHoursData } = result;
            
            const totalDurationSeconds = getTotalDurationSeconds(route);
            const totalDurationMinutes = secondsToMinutes(totalDurationSeconds);
              const maximum_Number = frequency > 0 ? Math.floor(totalDurationMinutes / frequency) : 0;
            const maxDeviationMeters = getMaxDeviationMeters(tolerance);

              // Process toilet data and filter using reusable functions
              const toiletsWithData = processToiletData(places, route);
              const filteredToilets = filterToilets(toiletsWithData, maxDeviationMeters, openingHoursData);

              // Process toilet selection using reusable function
              const topCandidates = processToiletSelection(filteredToilets, maximum_Number, totalDurationMinutes, maxDeviationMeters);
              const finalCandidates = topCandidates.map(({ score, ...rest }) => rest);

              // Sort final candidates and track intervals using reusable functions
              const sortedCandidates = sortCandidatesByDistance(finalCandidates);
              
              // Store opening hours and places for this route
              setOpeningHours(prev => ({ ...prev, [idx]: openingHoursData }));
              setPlacesByRoute(prev => ({ ...prev, [idx]: sortedCandidates }));

              // Track toilet availability for each frequency interval
              if (frequency > 0 && maximum_Number > 0) {
                const frequencyIntervals = trackFrequencyIntervals(sortedCandidates, maximum_Number);
                setToiletsAvailable(prev => ({ ...prev, [idx]: frequencyIntervals }));
              }

            const routeJson = JSON.stringify({
                routeIndex: idx,
                maximum_Number: maximum_Number,
                userPreferences: {
                  startLocation,
                  endLocation,
                  disabilityAccess,
                  frequency,
                  tolerance,
                  selectedTransport
                },
              toilets: finalCandidates.map(t => ({ ...t, place_id: t.place_id || (t.id ? `db_${t.id}` : t.place_id) }))
              }, null, 2);
            setRouteJsons(prev => ({ ...prev, [idx]: routeJson }));
          });
          if (limitedRoutes[1]) {
            searchToiletsForRoute(limitedRoutes[1], 1).then(result => {
              const { idx, places, route, openingHoursData } = result;
              const totalDurationSeconds = getTotalDurationSeconds(route);
              const totalDurationMinutes = secondsToMinutes(totalDurationSeconds);
              const maximum_Number = frequency > 0 ? Math.floor(totalDurationMinutes / frequency) : 0;
              const maxDeviationMeters = getMaxDeviationMeters(tolerance);

              // Process toilet data and filter using reusable functions
              const toiletsWithData = processToiletData(places, route);
              const filteredToilets = filterToilets(toiletsWithData, maxDeviationMeters, openingHoursData);
              // Process toilet selection using reusable function
              const topCandidates = processToiletSelection(filteredToilets, maximum_Number, totalDurationMinutes, maxDeviationMeters);
              const finalCandidates = topCandidates.map(({ score, ...rest }) => rest);
              // Sort final candidates and track intervals using reusable functions
              const sortedCandidates = sortCandidatesByDistance(finalCandidates);
              setOpeningHours(prev => ({ ...prev, [idx]: openingHoursData }));
              setPlacesByRoute(prev => ({ ...prev, [idx]: sortedCandidates }));
              const routeJson = JSON.stringify({
                routeIndex: idx,
                maximum_Number: maximum_Number,
                userPreferences: { startLocation, endLocation, disabilityAccess, frequency, tolerance, selectedTransport },
                toilets: finalCandidates.map(t => ({ ...t, place_id: t.place_id || (t.id ? `db_${t.id}` : t.place_id) }))
              }, null, 2);
              setRouteJsons(prev => ({ ...prev, [idx]: routeJson }));
              
              // Track toilet availability for each frequency interval
              if (frequency > 0 && maximum_Number > 0) {
                const frequencyIntervals = trackFrequencyIntervals(sortedCandidates, maximum_Number);
                setToiletsAvailable(prev => ({ ...prev, [idx]: frequencyIntervals }));
              }
            });
          }
          if (limitedRoutes[2]) {
            searchToiletsForRoute(limitedRoutes[2], 2).then(result => {
              const { idx, places, route, openingHoursData } = result;
              const totalDurationSeconds = getTotalDurationSeconds(route);
              const totalDurationMinutes = secondsToMinutes(totalDurationSeconds);
              const maximum_Number = frequency > 0 ? Math.floor(totalDurationMinutes / frequency) : 0;
              const maxDeviationMeters = getMaxDeviationMeters(tolerance);

              // Process toilet data and filter using reusable functions
              const toiletsWithData = processToiletData(places, route);
              const filteredToilets = filterToilets(toiletsWithData, maxDeviationMeters, openingHoursData);
              // Process toilet selection using reusable function
              const topCandidates = processToiletSelection(filteredToilets, maximum_Number, totalDurationMinutes, maxDeviationMeters);
              const finalCandidates = topCandidates.map(({ score, ...rest }) => rest);
              // Sort final candidates and track intervals using reusable functions
              const sortedCandidates = sortCandidatesByDistance(finalCandidates);
              setOpeningHours(prev => ({ ...prev, [idx]: openingHoursData }));
              setPlacesByRoute(prev => ({ ...prev, [idx]: sortedCandidates }));
              const routeJson = JSON.stringify({
                routeIndex: idx,
                maximum_Number: maximum_Number,
                userPreferences: { startLocation, endLocation, disabilityAccess, frequency, tolerance, selectedTransport },
                toilets: finalCandidates.map(t => ({ ...t, place_id: t.place_id || (t.id ? `db_${t.id}` : t.place_id) }))
              }, null, 2);
              setRouteJsons(prev => ({ ...prev, [idx]: routeJson }));
              
              // Track toilet availability for each frequency interval
              if (frequency > 0 && maximum_Number > 0) {
                const frequencyIntervals = trackFrequencyIntervals(sortedCandidates, maximum_Number);
                setToiletsAvailable(prev => ({ ...prev, [idx]: frequencyIntervals }));
              }
            });
          }
        } else {
          setTimeout(() => { throw new Error(`Directions request failed due to ${status}`); }, 0);
          setRoutes([]);
        }
      }
    );
  }, [startLocation, endLocation, selectedTransport, tolerance, disabilityAccess, frequency, findPlacesAlongRoute, addStartEndMarkers, addTransportChangeMarkers, calculateToiletRouteData, location.state, searchToiletsForRoute, processToiletSelection, processToiletData, filterToilets, sortCandidatesByDistance, trackFrequencyIntervals]);

  // Render selected route polyline once per selected route change
  useEffect(() => {
    if (!mapInstance.current) return;
    // Clear existing polylines
    polylines.current.forEach(pl => pl.setMap(null));
    polylines.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    try {
      routes.slice(0, 3).forEach((route, idx) => {
        if (!route || !route.legs) return;
        const path = [];
        route.legs.forEach(leg => {
          leg.steps.forEach(step => {
            if (Array.isArray(step.path)) {
              step.path.forEach(p => { path.push(p); bounds.extend(p); });
            }
          });
        });
        if (path.length > 0) {
          const polyline = new window.google.maps.Polyline({
            path,
            strokeColor: '#999999',
            strokeOpacity: 0.5,
            strokeWeight: 3,
            map: mapInstance.current,
          });
          polylines.current.push(polyline);
        }
      });
      allRoutesBoundsRef.current = bounds;
      hasFittedAllBoundsRef.current = false;
    } catch (e) {
      setTimeout(() => { throw new Error(`Failed to render route polylines: ${e.message || e}`); }, 0);
    }

    (async () => {
      if (!allRoutesBoundsRef.current) return;
      try {
        // Use passed coordinates (always available from autocomplete validation)
        if (startCoordinates) {
          allRoutesBoundsRef.current.extend(startCoordinates);
        }
        if (endCoordinates) {
          allRoutesBoundsRef.current.extend(endCoordinates);
        }
      } catch {}
      if (!hasFittedAllBoundsRef.current) {
        try { mapInstance.current.fitBounds(allRoutesBoundsRef.current); } catch {}
        hasFittedAllBoundsRef.current = true;
      }
    })();
  }, [routes, startCoordinates, endCoordinates]);




  useEffect(() => {
    if (!mapInstance.current) return;
    addStartEndMarkers();
    placeMarkers.current.forEach(m => m.setMap(null));
    placeMarkers.current = [];
    if (currentPlaces.length > 0) {
      // No bounds fitting here to avoid zoom jitter; only render markers
      currentPlaces.forEach(place => {
        try {
          const pid = place.place_id || (place.id ? `db_${place.id}` : undefined);
          const isSelected = pid ? !!selectedToilets[pid] : false;
          
          const icon = {
            url: `https://maps.google.com/mapfiles/ms/icons/${isSelected ? 'green' : 'yellow'}-dot.png`,
            scaledSize: new window.google.maps.Size(32, 32),
          };

          // Debug logging for marker creation
          let lat, lng;
          
          const location = extractToiletLocation(place);
          if (location) {
            lat = location.lat;
            lng = location.lng;
          } else if (place.location) {
            // Google Places with location property
            lat = typeof place.location.lat === 'function' 
              ? place.location.lat() 
              : place.location.lat;
            lng = typeof place.location.lng === 'function' 
              ? place.location.lng() 
              : place.location.lng;
          } else {
            setTimeout(() => { throw new Error(`No location found for place: ${JSON.stringify(place)}`); }, 0);
            return;
          }

          const marker = new window.google.maps.Marker({
            position: { lat: lat, lng: lng },
            map: mapInstance.current,
            title: place.name,
            icon: icon,
          });
          marker.addListener('click', () => toggleToiletSelection(place.place_id));
          placeMarkers.current.push(marker);
        } catch (error) {
          setTimeout(() => { throw new Error(`AI recommendation parsing error: ${error.message || error}`); }, 0);
        }
      });
    }
  }, [currentPlaces, selectedToilets, addStartEndMarkers]);

  // Function to parse AI recommendation and extract recommended toilet place_ids
  function parseAIRecommendation(aiResponse, availableToilets) {
    const recommendedPlaceIds = [];
    
    try {
      // Look for loo recommendations in the AI response
      let recommendationMatches = aiResponse.match(/🚏\s*Loo Recommendation No\.\s*\d+:\s*[^\n]+/g);
      
      // Fallback: accept lines without the emoji
      if (!recommendationMatches || recommendationMatches.length === 0) {
        const alt = aiResponse.match(/(^|\n)\s*Loo Recommendation No\.\s*\d+:\s*[^\n]+/g);
        if (alt && alt.length > 0) {
          recommendationMatches = alt;
        }
      }
      
      
      if (recommendationMatches && recommendationMatches.length > 0) {
        recommendationMatches.forEach(match => {
                  // Extract the loo name from the recommendation
        const nameMatch = match.match(/🚏\s*Loo Recommendation No\.\s*\d+:\s*(.+)/);
          if (nameMatch) {
            const recommendedName = nameMatch[1].trim();
            
            
            // Find matching toilet in available toilets
            const matchingToilet = availableToilets.find(toilet => 
              toilet.name.toLowerCase().includes(recommendedName.toLowerCase()) ||
              recommendedName.toLowerCase().includes(toilet.name.toLowerCase())
            );

            if (!matchingToilet) {
              const recLower = recommendedName.toLowerCase();
              availableToilets
                .filter(t => (t.name || '').toLowerCase().includes(recLower) || recLower.includes((t.name || '').toLowerCase()))
                .map(t => ({ name: t.name, place_id: t.place_id, id: t.id }));
            }
            
            if (matchingToilet) {
              const pid = matchingToilet.place_id || (matchingToilet.id ? `db_${matchingToilet.id}` : undefined);
              
              if (pid) recommendedPlaceIds.push(pid);
            } else {
                setTimeout(() => { throw new Error(`Could not find matching toilet for AI recommendation: "${recommendedName}"`); }, 0);
            }
          }
        });
      } else {
        // Final fallback: parse the "Selected Loo(s):" summary line
        const selectedLineMatch = aiResponse.match(/Selected\s+Loo\(s\):\s*([^\n]+)/i);
        
        if (selectedLineMatch) {
          const names = selectedLineMatch[1]
            .split(/[,;|]/)
            .map(s => s.trim())
            .filter(Boolean);
          
          names.forEach(recommendedName => {
            const recLower = (recommendedName || '').toLowerCase();
            const matchingToilet = availableToilets.find(toilet => {
              const n = (toilet.name || '').toLowerCase();
              return n.includes(recLower) || recLower.includes(n);
            });
          if (matchingToilet) {
              const pid = matchingToilet.place_id || (matchingToilet.id ? `db_${matchingToilet.id}` : undefined);
              
              if (pid) recommendedPlaceIds.push(pid);
            } else {
              
            }
          });
        }
      }
      
    } catch (error) {
      setTimeout(() => { throw error; }, 0);
    }
    
    return recommendedPlaceIds;
  }

  async function handleAIRecommendation(index) {
    setAiLoading(prev => ({ ...prev, [index]: true }));
    setAiRecommendation(prev => ({ ...prev, [index]: null }));
    
    const json = routeJsons[index];
    const routeData = JSON.parse(json);
    
    const currentToilets = routeData.toilets || [];
    const { tolerance, frequency, disabilityAccess } = routeData.userPreferences;
    const maximum_Number = routeData.maximum_Number;
    const maxDeviationMeters = getMaxDeviationMeters(tolerance);
    
    // Calculate total journey duration safely
    const totalJourneyDuration = currentToilets.length > 0 
      ? Math.round((currentToilets[0]?.timeElapsed || 0) + (currentToilets[0]?.timeRemaining || 0))
      : 0;

    
    const prompt = `You are an expert journey planner specializing in toilet stop optimization. Your task is to select the optimal toilet stops for a user's journey based on their specific preferences and constraints.

    **CRITICAL FIRST STEP - DISTANCE ANALYSIS:**
    Before making any recommendations, you MUST:
    1. Group all toilets by their frequencyInterval (such group being a 'frequency group')
    2. Compare the distanceFromRoute values within each group

    **JOURNEY CONTEXT:**
    - Start Location: ${startLocation}
    - End Location: ${endLocation}
    - Transport Mode: ${selectedTransport}
    - Total Journey Duration: ${Math.round(routeData.toilets[0]?.timeElapsed + routeData.toilets[0]?.timeRemaining || 0)} minutes
    - User's Maximum Deviation: ${maxDeviationMeters} meters from route
    - User's Stop Frequency: Every ${frequency} minutes (if frequency > 0)
    - Maximum Allowed Stops: ${maximum_Number}
    - Disability Access Required: ${disabilityAccess ? 'Yes' : 'No'}

    **DATA STRUCTURE AND ITERATION (MANDATORY):**
    - intervalKeys: sorted list of frequency intervals.
    - Iterate intervalKeys in ascending numerical order (e.g., 0, 1, 2...).
    - For each key k in intervalKeys, the candidate loos for that interval are toiletsByFrequency[k].
    - Select at most ONE loo per k (frequency group).
    - Use toilets (the flat array) only for cross-interval checks such as global spacing (MIN_SPACING) and any fallbacks that require comparing across all loos.

    **HARD CONSTRAINTS (MANDATORY):**
    - Never select more than ONE loo for the same interval key k.
    - Adjacent selected Time from Start values must be ≥ MIN_SPACING minutes apart.
    - If any constraint would be violated, re-select for the offending slot using the next-best candidate that satisfies all constraints. Do not output until all constraints pass.

    **DERIVED CONSTANTS (MANDATORY):**
    - TARGET_TIME for interval k is (k * ${frequency}) minutes from start.
    - WINDOW = ${Math.round(frequency * 0.30)} minutes (search window around TARGET_TIME for k).
    - MIN_SPACING = ${Math.round(frequency * 0.70)} minutes (minimum allowable spacing between any two selected loos).

    **SELECTION CRITERIA FOR EACH FREQUENCY GROUP:**

    2. **FEASIBILITY CHECK:**
       - Distance from route must be ≤ ${maxDeviationMeters} meters
       - **MANDATORY**: Must recommend EXACTLY ${Math.round(routeData.toilets[0]?.timeElapsed + routeData.toilets[0]?.timeRemaining || 0) <= frequency ? 1 : Math.floor((routeData.toilets[0]?.timeElapsed + routeData.toilets[0]?.timeRemaining || 0) / frequency)} stop(s) - NO EXCEPTIONS
       - Must not exceed ${maximum_Number} total stops

    2. **OPTIMAL TIMING & FREQUENCY SPACING:**
       - **FREQUENCY SPACING**: Each selected toilet must be spaced approximately ${frequency} minutes apart 
       - **CRITICAL RULE**: If journey duration ≤ frequency, recommend maximum 1 stop
       - **CRITICAL RULE**: If journey duration = frequency exactly, recommend 1 stop as close to ${frequency} minutes as possible

    3. **SELECTION LOGIC:**
       - **FREQUENCY-BASED SELECTION (GROUPED)**: For each interval key k in intervalKeys (ascending), consider toiletsByFrequency[k] as the eligible group for that slot.
       - **TARGET WINDOW**: Prefer loos within [TARGET_TIME(k) − WINDOW, TARGET_TIME(k) + WINDOW]. If none exist, use the closest timeElapsed overall that still respects MIN_SPACING and distance.
       - **ONE TOILET PER GROUP**: From toiletsByFrequency[k], select ONLY ONE loo following the distance/timing rules. Use toilets (flat) for global spacing checks or fallback selection when a group is empty.
       - **MIN_SPACING COMPLIANCE**: If the best candidate for k would violate MIN_SPACING with the previously selected loo, choose the next-best candidate within toiletsByFrequency[k] that complies. If none comply, skip this slot and continue.
       - **SPACING ENFORCEMENT**: After selecting for any interval k, mark loos with timeElapsed ∈ [selected.timeElapsed − MIN_SPACING, selected.timeElapsed + MIN_SPACING] as ineligible for later intervals.
  
    **CLEANLINESS (TIE-BREAKER, SIMPLE):**
    - When distance and timing are otherwise equal, prefer predicted cleanliness by establishment type inferred from name/venueType:
      - LOW: Public Toilet, Restroom, WC
      - MEDIUM: Station, Service/Petrol Station
      - HIGH: Cafe, Restaurant, Hotel, Pub/Bar
    - If multiple loos have the same predicted cleanliness, select the toilet with known opening hours first 

    **RECOMMENDATION FORMAT:**
    Please provide your recommendations in exactly this format with clear separation between criteria. Please refer to 'loos' rather than 'toilets' in your responses:

    🚏 Loo Recommendation No. x: [Name of Establishment]
    
    Address: [Address]
    
    Distance from Route: [x meters]
    
    Time from Start: [x minutes]
    
    Time Remaining: [x minutes]

    🏁 Route Summary:
    Selected Loo(s): [list all recommended loos]
    Total Stops: [MUST BE EXACTLY ${Math.round(routeData.toilets[0]?.timeElapsed + routeData.toilets[0]?.timeRemaining || 0) <= frequency ? 1 : Math.floor((routeData.toilets[0]?.timeElapsed + routeData.toilets[0]?.timeRemaining || 0) / frequency)}]
    Explanation: [Explain your selection strategy, why these loos were chosen over others, how they meet the user's preferences (including known opening hours and predicted cleanliness). List the chosen Time from Start (which should increase between selected loos) values and their pairwise differences; none should be less than MIN_SPACING. List the selected frequencyInterval values and explicitly confirm they are all different. If you selected multiple loos for the same frequency interval, explain why.]

    **CRITICAL INSTRUCTIONS - READ CAREFULLY:**
    - **FREQUENCY SPACING**: Stops must be spaced approximately ${frequency} minutes apart (target: ${frequency}, ${frequency * 2}, ${frequency * 3}, etc.)
    - If no loos meet the criteria, explain why and suggest alternatives.`.trim();
    try {
      // Create enhanced data for AI recommendation
      // Build grouped view by frequency interval (non-breaking addition)
      const toInterval = (t) => {
        if (Number.isFinite(t?.frequencyInterval)) return t.frequencyInterval;
        const te = Number.isFinite(t?.timeElapsed) ? t.timeElapsed : 0;
        return frequency > 0 ? Math.floor(te / frequency) : 0;
      };
      const toiletsByFrequency = currentToilets.reduce((acc, t) => {
        const key = String(toInterval(t));
        if (!acc[key]) acc[key] = [];
        acc[key].push(t);
        return acc;
      }, {});
      const intervalKeys = Object.keys(toiletsByFrequency)
        .map(k => Number(k))
        .sort((a, b) => a - b)
        .map(String);
      const intervalCounts = Object.fromEntries(intervalKeys.map(k => [k, toiletsByFrequency[k].length]));

      const enhancedData = {
        ...routeData,
        journeyContext: {
          startLocation,
          endLocation,
          transportMode: selectedTransport,
          totalDuration: totalJourneyDuration,
          userPreferences: {
            maxDeviationMeters,
            frequency,
            maximumStops: maximum_Number,
            disabilityAccess
          }
        },
        toilets: currentToilets.map(place => {
          // Check if this is a database toilet and has opening hours data
          let hasKnownOpeningHours = false;
          if (place.place_id && place.place_id.startsWith('db_') && openingHours[index] && openingHours[index][place.id]) {
            hasKnownOpeningHours = true;
          }

          // Determine if this is a database toilet or Google Places toilet
          const isDatabaseToilet = place.place_id && place.place_id.startsWith('db_');

          return {
          place_id: place.place_id,
          name: place.name,
          address: place.address || place.vicinity || place.formatted_address,
          location: place.geometry?.location,
          distanceFromRoute: place.distanceFromRoute,
          timeElapsed: place.timeElapsed,
          timeRemaining: place.timeRemaining,
          frequencyInterval: place.frequencyInterval,

            // Database-specific properties (with defaults for Google Places)
            is_accessible: isDatabaseToilet ? (place.is_accessible || false) : false,
            is_free: isDatabaseToilet ? (place.is_free !== undefined ? place.is_free : true) : true,
            cleanliness_rating: isDatabaseToilet ? place.cleanliness_rating : null,
          areas: place.areas,
          // Additional context for AI
          venueType: place.types?.[0] || 'unknown',
          rating: place.rating,
          userRatingsTotal: place.user_ratings_total,
            priceLevel: place.price_level,
            // Opening hours information for AI decision making
            hasKnownOpeningHours: hasKnownOpeningHours
          };
        }),
        toiletsByFrequency,
        intervalKeys,
        intervalCounts
      };
      // debug removed
      
      // debug removed
      
      // Include Supabase JWT for server-side auth
      let authHeader = {};
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;
        if (accessToken) {
          authHeader = { Authorization: `Bearer ${accessToken}` };
        }
      } catch (e) {
        // no-op; request will likely be rejected by server without auth
      }
      
      const res = await fetch('/api/ai/recommendation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ 
          json: JSON.stringify(enhancedData), 
          prompt 
        })
      });
      
      const data = await res.json();

      setAiRecommendation(prev => ({ ...prev, [index]: data.recommendation }));
      
      // Parse AI response and automatically select recommended toilets for this specific route only
      if (data.recommendation) {
        
        const recommendedToilets = parseAIRecommendation(data.recommendation, currentToilets);
        
          
        if (recommendedToilets.length > 0) {
          // Replace selections with only this route's AI selections
          setSelectedToilets(prev => {
            const updated = {};
          recommendedToilets.forEach(placeId => {
            const toiletBelongsToRoute = currentToilets.some(toilet => toilet.place_id === placeId);
            if (toiletBelongsToRoute) {
                updated[placeId] = true;
              }
            });
            return updated;
          });
        } else {
          
        }
      }
    } catch (err) {
      setTimeout(() => { throw err; }, 0);
      setAiRecommendation(prev => ({ ...prev, [index]: 'Error getting AI recommendation.' }));
    } finally {
      setAiLoading(prev => ({ ...prev, [index]: false }));
    }
  }

  const containerStyle = {
    padding: 0,
    backgroundColor: '#f5f0f0',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
    gap: 0,
    position: 'relative',
  };

  const contentStyle = {
    flex: 1,
    maxWidth: window.innerWidth <= 768 ? '100%' : '800px',
    marginRight: window.innerWidth <= 768 ? '0' : '2rem',
    backgroundColor: '#fff',
    borderRadius: window.innerWidth <= 768 ? '0' : '8px',
    boxShadow: window.innerWidth <= 768 ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
    padding: window.innerWidth <= 768 ? '1rem' : '2rem',
    order: window.innerWidth <= 768 ? 2 : 1,
    position: window.innerWidth <= 768 ? 'fixed' : 'relative',
    top: 0,
    right: window.innerWidth <= 768 ? (collapsedRoutes.mobile ? '-100%' : '0') : 'auto',
    width: window.innerWidth <= 768 ? '85%' : 'auto',
    height: window.innerWidth <= 768 ? '100vh' : 'auto',
    zIndex: window.innerWidth <= 768 ? 1004 : 'auto',
    transition: window.innerWidth <= 768 ? 'right 0.3s ease' : 'none',
    overflowY: window.innerWidth <= 768 ? 'auto' : 'visible',
  };

  const mapContainerStyle = {
    flex: 1,
    height: window.innerWidth <= 768 ? '100vh' : 'calc(100vh - 4rem)',
    borderRadius: window.innerWidth <= 768 ? '0' : '8px',
    boxShadow: window.innerWidth <= 768 ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
    order: window.innerWidth <= 768 ? 1 : 2,
    position: window.innerWidth <= 768 ? 'fixed' : 'relative',
    top: 0,
    left: 0,
    right: 0,
    zIndex: window.innerWidth <= 768 ? 1 : 'auto',
  };

  const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#004C10',
    cursor: 'pointer',
    fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
    fontFamily: '"Zen Old Mincho", serif',
    marginBottom: window.innerWidth <= 768 ? '1rem' : '2rem',
    padding: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
    '&:hover': {
      opacity: 0.8,
    },
  };

  const titleStyle = {
    color: '#004C10',
    fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem',
    fontFamily: '"Zen Old Mincho", serif',
    marginBottom: window.innerWidth <= 768 ? '1rem' : '1.5rem',
  };

  const resultsContainerStyle = {
    marginTop: window.innerWidth <= 768 ? '1rem' : '2rem',
    color: '#004C10',
  };

  const buttonStyle = {
    marginTop: window.innerWidth <= 768 ? '0.75rem' : '1rem',
    padding: window.innerWidth <= 768 ? '0.4rem 0.8rem' : '0.5rem 1rem',
    backgroundColor: '#004C10',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem',
    fontFamily: '"Zen Old Mincho", serif',
  };

  const mobileButtonStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.3rem',
    padding: '0.5rem',
    backgroundColor: '#004C10',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontFamily: '"Zen Old Mincho", serif',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
  };

  const toiletCardStyle = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
    cursor: 'pointer',
    padding: window.innerWidth <= 768 ? '0.6rem' : '0.75rem',
    backgroundColor: isSelected ? '#e8f5e9' : '#f8f9fa',
    borderRadius: '8px',
    border: isSelected ? '2px solid #28a745' : '1px solid #e9ecef',
    transition: 'all 0.2s ease',
  });

  useEffect(() => {
    polylines.current.forEach((pl, idx) => {
      pl.setOptions({
        strokeColor: idx === selectedRouteIndex ? '#004C10' : '#999999',
        strokeOpacity: idx === selectedRouteIndex ? 0.9 : 0.5,
        strokeWeight: idx === selectedRouteIndex ? 4 : 3,
      });
    });
  }, [selectedRouteIndex]);

  return (
    <div style={containerStyle}>
      {/* Mobile Toggle Button - Only visible on mobile */}
      {window.innerWidth <= 768 && (
        <button
          onClick={toggleMobilePanel}
          disabled={!markersRendered}
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1003,
            backgroundColor: markersRendered ? '#004C10' : '#cccccc',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '3rem',
            height: '3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            cursor: markersRendered ? 'pointer' : 'not-allowed',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease',
            opacity: markersRendered ? 1 : 0.6,
          }}
          onMouseEnter={(e) => {
            if (markersRendered) {
              e.target.style.transform = 'scale(1.1)';
            }
          }}
          onMouseLeave={(e) => {
            if (markersRendered) {
              e.target.style.transform = 'scale(1)';
            }
          }}
        >
          {collapsedRoutes.mobile ? '☰' : '✕'}
        </button>
      )}

      {/* Mobile Backdrop - Only visible when panel is open */}
      {window.innerWidth <= 768 && !collapsedRoutes.mobile && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1001,
            cursor: markersRendered ? 'pointer' : 'not-allowed',
          }}
          onClick={markersRendered ? toggleMobilePanel : undefined}
        />
      )}

      <div style={contentStyle}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: window.innerWidth <= 768 ? '1rem' : '2rem',
        }}>
          <button 
            style={backButtonStyle} 
            onClick={() => navigate('/')}
          >
            <FaArrowLeft />
            Back to Search
          </button>
          
          {/* Mobile Close Button - Only visible on mobile when panel is open */}
          {window.innerWidth <= 768 && (
            <button
              onClick={toggleMobilePanel}
              disabled={!markersRendered}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: markersRendered ? '#004C10' : '#cccccc',
                fontSize: '1.5rem',
                cursor: markersRendered ? 'pointer' : 'not-allowed',
                padding: '0.5rem',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: markersRendered ? 1 : 0.6,
              }}
            >
              ✕
            </button>
          )}
        </div>
        <h1 style={titleStyle}>Route Results</h1>
        
        {/* Mobile Navigation Buttons - Only visible on mobile */}
        {window.innerWidth <= 768 && user && (
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <Link
              to="/add-toilet"
              style={mobileButtonStyle}
              className="mobile-button-hover"
            >
              <FaPlus size={14} />
              Add Toilet
            </Link>
            
            <button
              onClick={logout}
              style={mobileButtonStyle}
              className="mobile-button-hover"
            >
              <FaSignOutAlt size={14} />
              Logout
            </button>
          </div>
        )}
        
        {/* Search Parameters Summary */}
        {location.state && (
          <div className="search-params" style={{
            padding: window.innerWidth <= 768 ? '0.75rem' : '1rem',
            marginBottom: window.innerWidth <= 768 ? '1rem' : '1.5rem'
          }}>
            <div className="search-params-title" style={{
              fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
              marginBottom: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
              gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem'
            }}>
              <span className="param-icon" style={{fontSize: '1.1rem'}}>🔍</span>
              Search Parameters
            </div>
            
            <div className="search-params-grid" style={{
              gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(auto-fit, minmax(140px, 1fr))' : 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
              fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.85rem'
            }}>
              <div className="param-row" style={{gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem'}}>
                <span className="param-icon" style={{fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>📍</span>
                <div>
                  <div className="param-label" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>Start:</div>
                  <div className="param-value" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>{startLocation}</div>
                </div>
              </div>
              
              <div className="param-row" style={{gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem'}}>
                <span className="param-icon" style={{fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>🎯</span>
                <div>
                  <div className="param-label" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>End:</div>
                  <div className="param-value" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>{endLocation}</div>
                </div>
              </div>
              
              <div className="param-row" style={{gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem'}}>
                <span className="param-icon" style={{fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>{getTransportIcon(selectedTransport)}</span>
                <div>
                  <div className="param-label" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>Transport Mode:</div>
                  <div className="param-value" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>{getTransportDisplayText(selectedTransport)}</div>
                </div>
              </div>
              
              <div className="param-row" style={{gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem'}}>
                <span className="param-icon" style={{fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>♿</span>
                <div>
                  <div className="param-label" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>Disability Access:</div>
                  <div className="param-value" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>{disabilityAccess ? 'Yes' : 'No'}</div>
                </div>
              </div>
              
              <div className="param-row" style={{gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem'}}>
                <span className="param-icon" style={{fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>🔄</span>
                <div>
                  <div className="param-label" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>Frequency:</div>
                  <div className="param-value" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>Every {frequency} minutes</div>
                </div>
              </div>
              
              <div className="param-row" style={{gap: window.innerWidth <= 768 ? '0.3rem' : '0.5rem'}}>
                <span className="param-icon" style={{fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'}}>↔️</span>
                <div>
                  <div className="param-label" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>Tolerance:</div>
                  <div className="param-value" style={{fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem'}}>{tolerance * 10} metres</div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {location.state ? (
          <div className="results-container" style={resultsContainerStyle}>
            {routes.map((route, index) => {
              const transitModes = getTransitModes(route);
              return (
                                <div 
                  key={index} 
                  className={`route-card ${selectedRouteIndex === index ? 'selected' : ''}`}
                  style={{
                    padding: window.innerWidth <= 768 ? '0.75rem' : '1rem', 
                    marginBottom: window.innerWidth <= 768 ? '1rem' : '1.5rem'
                  }}
                  onClick={() => {
                    setSelectedRouteIndex(index);
                    // Trigger toilet search for this route if not already done
                    if (!placesByRoute[index] || placesByRoute[index].length === 0) {
                      searchToiletsForRoute(routes[index], index).then(result => {
                        const { idx, places, route, openingHoursData } = result;
                        
                        const totalDurationSeconds = getTotalDurationSeconds(route);
                        const totalDurationMinutes = secondsToMinutes(totalDurationSeconds);
                        const maximum_Number = frequency > 0 ? Math.floor(totalDurationMinutes / frequency) : 0;
                        const maxDeviationMeters = getMaxDeviationMeters(tolerance);

                        const toiletsWithData = places.map(place => {
                          const toiletLocation = extractToiletLocation(place);
                          const routeData = calculateToiletRouteData(place, route);

                          return {
                            ...place,
                            place_id: place.place_id,
                            name: place.name,
                            address: place.vicinity || place.formatted_address,
                            location: toiletLocation,
                            ...routeData
                          };
                        });

                        // Pre-filter toilets that are outside the user's tolerance and are closed
                        const filteredToilets = toiletsWithData.filter(toilet => {
                          if (toilet.distanceFromRoute === null || toilet.distanceFromRoute > maxDeviationMeters) {
                            return false;
                          }
                          
                          if (toilet.place_id && toilet.place_id.startsWith('db_')) {
                            if (openingHoursData[toilet.id]) {
                              const { journeyDate, journeyTime } = location.state || {};
                              const openStatus = checkIfToiletIsOpen(openingHoursData[toilet.id], journeyDate, journeyTime);
                              if (!openStatus.isOpen) {
                                return false;
                              }
                            }
                          }
                          
                          return true;
                        });

                        // Scoring and selection logic
                        const stopInterval = maximum_Number > 0 ? totalDurationMinutes / (maximum_Number + 1) : totalDurationMinutes;
                        const scoredToilets = filteredToilets.map(toilet => {
                          let minTimeDiff = Infinity;
                          if (maximum_Number > 0) {
                            for (let i = 1; i <= maximum_Number; i++) {
                              const idealTime = stopInterval * i;
                              const timeDiff = Math.abs(toilet.timeElapsed - idealTime);
                              if (timeDiff < minTimeDiff) {
                                minTimeDiff = timeDiff;
                              }
                            }
                          } else {
                            minTimeDiff = 0;
                          }
                          
                          const normalizedDistance = toilet.distanceFromRoute / maxDeviationMeters;
                          const normalizedTimeDiff = totalDurationMinutes > 0 ? minTimeDiff / totalDurationMinutes : 0;
                          const score = (0.5 * normalizedDistance) + (0.5 * normalizedTimeDiff);
                          return { ...toilet, score };
                        });

                        const topCandidates = scoredToilets.sort((a, b) => a.score - b.score).slice(0, 10);
                        const finalCandidates = topCandidates.map(({ score, ...rest }) => rest);

                        const sortedCandidates = finalCandidates.sort((a, b) => {
                          if (a.distanceFromRoute === null && b.distanceFromRoute === null) return 0;
                          if (a.distanceFromRoute === null) return 1;
                          if (b.distanceFromRoute === null) return -1;
                          return a.distanceFromRoute - b.distanceFromRoute;
                        });

                        // Update state with the new toilet data
                        setPlacesByRoute(prev => ({ ...prev, [idx]: sortedCandidates }));
                        setOpeningHours(prev => ({ ...prev, [idx]: openingHoursData }));
                        setCombinedToilets(prev => ({ ...prev, [idx]: sortedCandidates }));
                      });
                    }
                  }}
                >
                  {/* Route Header */}
                  <div className="route-header" style={{
                    marginBottom: window.innerWidth <= 768 ? '0.75rem' : '1rem',
                    padding: window.innerWidth <= 768 ? '0.5rem' : '0.75rem'
                  }}>
                    <div className="route-title" style={{
                      fontSize: window.innerWidth <= 768 ? '1.1rem' : '1.3rem',
                      marginRight: window.innerWidth <= 768 ? '0.5rem' : '1rem'
                    }}>
                      Route {index + 1}
                    </div>
                    
                    {/* Collapse/Expand Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRouteCollapse(index);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem',
                        cursor: 'pointer',
                        color: '#004C10',
                        marginRight: window.innerWidth <= 768 ? '0.3rem' : '0.5rem',
                        padding: window.innerWidth <= 768 ? '0.2rem' : '0.25rem',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e8f5e9';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      {collapsedRoutes[index] ? '▶' : '▼'}
                    </button>
                    
                    {/* Route Stats */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      flex: 1
                    }}>
                      {transitModes && (
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span style={{fontSize: '1rem'}}>🚌</span>
                          <span>{transitModes}</span>
                        </div>
                      )}
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        fontSize: '0.9rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#004C10',
                          fontWeight: '500'
                        }}>
                          <span style={{fontSize: '1.1rem'}}>📏</span>
                          <span>{getTotalDistanceText(route)}</span>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: '#004C10',
                          fontWeight: '500'
                        }}>
                          <span style={{fontSize: '1.1rem'}}>⏱️</span>
                          <span>{getTotalDurationText(route)}</span>
                        </div>
                      </div>
                      
                      {/* Frequency interval availability message */}
                      {frequency > 0 && toiletsAvailable[index] && (
                        <div style={{
                          marginTop: '0.5rem',
                          fontSize: '0.8rem',
                          color: '#666'
                        }}>
                          {(() => {
                            const intervals = toiletsAvailable[index];
                            const missingIntervals = Object.entries(intervals)
                              .filter(([interval, available]) => !available)
                              .map(([interval]) => (parseInt(interval) + 1).toString());
                            
                            if (missingIntervals.length === 0) {
                              return;
                            } else if (missingIntervals.length > 0 && missingIntervals.length < Object.keys(intervals).length) {
                              const formattedIntervals = missingIntervals.length > 1 
                                ? missingIntervals.slice(0, -1).join(', ') + ' and ' + missingIntervals[missingIntervals.length - 1]
                                : missingIntervals[0];
                              return <span style={{color: '#ffc107'}}>⚠️ Missing loos for stop number(s): {formattedIntervals}. Consider adjusting frequency or tolerance.</span>;
                            }
                          })()}
                        </div>
                      )}
                      
                      {/* Message when frequency exceeds journey time */}
                      {frequency > 0 && (() => {
                        const route = routes[index];
                        if (!route) return false;
                        const totalDurationSeconds = getTotalDurationSeconds(route);
                        const totalDurationMinutes = secondsToMinutes(totalDurationSeconds);
                        const maximum_Number = Math.floor(totalDurationMinutes / frequency);
                        return maximum_Number <= 0;
                      })() && (
                        <div style={{
                          marginTop: '0.5rem',
                          fontSize: '0.8rem',
                          color: '#dc3545'
                        }}>
                          ⚠️ Frequency of loo stops ({frequency} min) exceeds journey time, so no stops are required. Consider reducing frequency if needed.
                        </div>
                      )}
                      
                      {/* Message when no toilets found due to tolerance or other issues */}
                      {placesByRoute[index] && placesByRoute[index].length === 0 && toiletsAvailable[index] && (
                        <div style={{
                          marginTop: '0.5rem',
                          fontSize: '0.8rem',
                          color: '#dc3545'
                        }}>
                          ⚠️ No loos found based on user preferences. Consider increasing tolerance or adjusting other route parameters.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  {!collapsedRoutes[index] && (
                    <>
                      {/* Button Container */}
                      <div style={{ 
                        marginTop: '0.75rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                    {/* Main Google Maps Button */}
                    <div style={{ 
                      display: 'flex',
                      gap: '0.5rem',
                      justifyContent: 'space-around'
                    }}>
                      <button
                        onClick={() => {
                          // Map transport modes to Google Maps travel modes
                          let travelMode;
                          switch (selectedTransport) {
                            case 'walking':
                              travelMode = 'walking';
                              break;
                            case 'cycling':
                              travelMode = 'bicycling';
                              break;
                            case 'driving':
                              travelMode = 'driving';
                              break;
                            case 'public':
                              travelMode = 'transit';
                              break;
                            default:
                              travelMode = 'driving';
                          }
                          
                          // Get selected toilets for this route
                          const selectedToiletsForRoute = placesByRoute[index]?.filter(place => selectedToilets[place.place_id]) || [];
                          
                          if (selectedTransport === 'public' && selectedToiletsForRoute.length > 0) {
                            // For public transport, create multiple route segments
                            const segments = [];
                            let currentOrigin = startLocation;
                            
                            selectedToiletsForRoute.forEach((toilet, toiletIndex) => {
                              let lat, lng;
                              if (toilet.geometry && toilet.geometry.location) {
                                lat = typeof toilet.geometry.location.lat === 'function' 
                                  ? toilet.geometry.location.lat() 
                                  : toilet.geometry.location.lat;
                                lng = typeof toilet.geometry.location.lng === 'function' 
                                  ? toilet.geometry.location.lng() 
                                  : toilet.geometry.location.lng;
                              } else if (toilet.location) {
                                lat = typeof toilet.location.lat === 'function' 
                                  ? toilet.location.lat() 
                                  : toilet.location.lat;
                                lng = typeof toilet.location.lng === 'function' 
                                  ? toilet.location.lng() 
                                  : toilet.location.lng;
                              }
                              
                              if (lat && lng) {
                                const toiletLocation = `${lat},${lng}`;
                                segments.push({
                                  from: currentOrigin,
                                  to: toiletLocation,
                                  name: toilet.name,
                                  description: `Segment ${toiletIndex + 1}: To ${toilet.name}`,
                                  url: `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(currentOrigin)}&destination=${encodeURIComponent(toiletLocation)}&travelmode=transit`
                                });
                                currentOrigin = toiletLocation;
                              }
                            });
                            
                            // Final segment to destination
                            segments.push({
                              from: currentOrigin,
                              to: endLocation,
                              name: 'Destination',
                              description: `Final segment: To destination`,
                              url: `https://www.google.maps/dir/?api=1&origin=${encodeURIComponent(currentOrigin)}&destination=${encodeURIComponent(endLocation)}&travelmode=transit`
                            });
                            
                            // Open first segment and show others in console for now
                            window.open(segments[0].url, '_blank');
                            
                            // Show remaining segments as additional options
                            if (segments.length > 1) {
                              // Future: present remainingSegments as options
                            }
                          } else {
                            // For non-public transport or no selected toilets, use waypoints approach
                            let waypoints = '';
                            if (selectedToiletsForRoute.length > 0) {
                              const waypointCoords = selectedToiletsForRoute.map(place => {
                                let lat, lng;
                                        const location = extractToiletLocation(place);
        if (location) {
          lat = location.lat;
          lng = location.lng;
                                } else if (place.location) {
                                  lat = typeof place.location.lat === 'function' 
                                    ? place.location.lat() 
                                    : place.location.lat;
                                  lng = typeof place.location.lng === 'function' 
                                    ? place.location.lng() 
                                    : place.location.lng;
                                }
                                return `${lat},${lng}`;
                              }).filter(coord => coord !== 'undefined,undefined');
                              
                              if (waypointCoords.length > 0) {
                                waypoints = `&waypoints=${waypointCoords.join('|')}`;
                              }
                            }
                            
                            const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(startLocation)}&destination=${encodeURIComponent(endLocation)}&travelmode=${travelMode}${waypoints}`;
                            window.open(url, '_blank');
                          }
                        }}
                        style={{
                          ...buttonStyle,
                          backgroundColor: '#004C10',
                          color: '#fff',
                          fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem',
                          padding: window.innerWidth <= 768 ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                          marginTop: 0,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#e8f5e9';
                          e.target.style.color = '#004C10';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#004C10';
                          e.target.style.color = '#fff';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        Open in Google Maps
                      </button>

                      {/* AI Recommendation Button with tooltip */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        style={{ 
                          ...buttonStyle, 
                          backgroundColor: hasToiletsForRoute(index) ? '#004C10' : '#cccccc',
                          color: '#fff',
                          fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem', 
                          padding: window.innerWidth <= 768 ? '0.4rem 0.6rem' : '0.5rem 0.75rem',
                          marginTop: 0,
                          transition: 'all 0.2s ease'
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          // Deselect all currently selected loos across all routes
                          setSelectedToilets(() => ({}));
                          handleAIRecommendation(index);
                        }}
                        disabled={aiLoading[index] || !hasToiletsForRoute(index)}
                        onMouseEnter={(e) => {
                          if (!aiLoading[index] && hasToiletsForRoute(index)) {
                            e.target.style.backgroundColor = '#e8f5e9';
                            e.target.style.color = '#004C10';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!aiLoading[index] && hasToiletsForRoute(index)) {
                            e.target.style.backgroundColor = '#004C10';
                            e.target.style.color = '#fff';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {aiLoading[index] ? 'Getting AI Recommendation...' : 'AI Recommendation'}
                      </button>
                      <div 
                        style={{ position: 'relative', display: 'inline-block' }}
                        onMouseEnter={(e) => {
                          const tip = e.currentTarget.querySelector('.ai-tip');
                          if (tip) {
                            tip.style.visibility = 'visible';
                            tip.style.opacity = 1;
                            tip.style.transform = 'translateY(-2px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          const tip = e.currentTarget.querySelector('.ai-tip');
                          if (tip) {
                            tip.style.visibility = 'hidden';
                            tip.style.opacity = 0;
                            tip.style.transform = 'translateY(0)';
                          }
                        }}
                        onTouchStart={(e) => {
                          const tip = e.currentTarget.querySelector('.ai-tip');
                          if (tip) {
                            tip.style.visibility = 'visible';
                            tip.style.opacity = 1;
                          }
                        }}
                        onTouchEnd={(e) => {
                          const tip = e.currentTarget.querySelector('.ai-tip');
                          if (tip) {
                            tip.style.visibility = 'hidden';
                            tip.style.opacity = 0;
                          }
                        }}
                      >
                        <FaInfoCircle style={{ color: '#6c757d', cursor: 'pointer' }} />
                        <div className="ai-tip" style={{
                          position: 'absolute',
                          top: '-2.2rem',
                          left: '0',
                          background: '#fff',
                          color: '#004C10',
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          borderRadius: 6,
                          padding: window.innerWidth <= 768 ? '6px 8px' : '8px 10px',
                          fontSize: window.innerWidth <= 768 ? '0.68rem' : '0.78rem',
                          maxWidth: window.innerWidth <= 768 ? '220px' : '280px',
                          zIndex: 1000,
                          visibility: 'hidden',
                          opacity: 0,
                          transition: 'all 0.15s ease',
                          pointerEvents: 'auto'
                        }}>
                          Beta feature: AI considers known opening hours and predicted cleanliness (by establishment type), alongside your selected parameters.
                        </div>
                      </div>
                      </div>
                    </div>

                                         {/* Individual Leg Buttons for Public Transport */}
                     {selectedTransport === 'public' && placesByRoute[index]?.filter(place => selectedToilets[place.place_id]).length > 0 && (
                       <div style={{
                         marginTop: '0.5rem',
                         padding: '0.75rem',
                         backgroundColor: '#f8f9fa',
                         borderRadius: '6px',
                         border: '1px solid #e9ecef'
                       }}>

                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.5rem',
                          justifyContent: 'center'
                        }}>
                          {(() => {
                            const selectedToiletsForRoute = placesByRoute[index]?.filter(place => selectedToilets[place.place_id]) || [];
                            const segments = [];
                            let currentOrigin = startLocation;
                            
                            // Create segments for each selected toilet
                            selectedToiletsForRoute.forEach((toilet, toiletIndex) => {
                              let lat, lng;
                              if (toilet.geometry && toilet.geometry.location) {
                                lat = typeof toilet.geometry.location.lat === 'function' 
                                  ? toilet.geometry.location.lat() 
                                  : toilet.geometry.location.lat;
                                lng = typeof toilet.geometry.location.lng === 'function' 
                                  ? toilet.geometry.location.lng() 
                                  : toilet.geometry.location.lng;
                              } else if (toilet.location) {
                                lat = typeof toilet.location.lat === 'function' 
                                  ? toilet.location.lat() 
                                  : toilet.location.lat;
                                lng = typeof toilet.location.lng === 'function' 
                                  ? toilet.location.lng() 
                                  : toilet.location.lng;
                              }
                              
                              if (lat && lng) {
                                const toiletLocation = `${lat},${lng}`;
                                segments.push({
                                  legNumber: toiletIndex + 1,
                                  from: currentOrigin,
                                  to: toiletLocation,
                                  name: toilet.name,
                                  description: `To ${toilet.name}`,
                                  url: `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(currentOrigin)}&destination=${encodeURIComponent(toiletLocation)}&travelmode=transit`
                                });
                                currentOrigin = toiletLocation;
                              }
                            });
                            
                            // Add final segment to destination
                            segments.push({
                              legNumber: segments.length + 1,
                              from: currentOrigin,
                              to: endLocation,
                              name: 'Destination',
                              description: 'To destination',
                              url: `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(currentOrigin)}&destination=${encodeURIComponent(endLocation)}&travelmode=transit`
                            });
                            
                            return segments.map((segment, segmentIndex) => (
                                                             <button
                                 key={segmentIndex}
                                 onClick={() => window.open(segment.url, '_blank')}
                                 style={{
                                   ...buttonStyle,
                                   backgroundColor: '#e8f5e9',
                                   color: '#004C10',
                                   fontSize: '0.75rem',
                                   padding: '0.4rem 0.6rem',
                                   marginTop: 0,
                                   transition: 'all 0.2s ease',
                                   minWidth: '60px',
                                   border: '1px solid #004C10'
                                 }}
                                 title={`Leg ${segment.legNumber}: ${segment.description}`}
                                 onMouseEnter={(e) => {
                                   e.target.style.backgroundColor = '#c8e6c9';
                                   e.target.style.transform = 'translateY(-1px)';
                                   e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                                 }}
                                 onMouseLeave={(e) => {
                                   e.target.style.backgroundColor = '#e8f5e9';
                                   e.target.style.transform = 'translateY(0)';
                                   e.target.style.boxShadow = 'none';
                                 }}
                               >
                                 Leg {segment.legNumber}
                               </button>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI Recommendation Result */}
                  {aiRecommendation[index] && (
                    <div style={{ 
                      background: '#e8f5e9', 
                      color: '#004C10', 
                      borderRadius: 4, 
                      padding: window.innerWidth <= 768 ? 8 : 12, 
                      fontSize: window.innerWidth <= 768 ? 12 : 14, 
                      marginTop: window.innerWidth <= 768 ? 6 : 8 
                    }}>
                      <ReactMarkdown>{aiRecommendation[index]}</ReactMarkdown>
                    </div>
                  )}

                  {placesByRoute[index] && placesByRoute[index].length > 0 && (
                    <div style={{marginTop: '1rem'}}>
                      
                      {/* Unified Toilet List */}
                      <div style={{marginBottom: '1rem'}}>
                        {placesByRoute[index].map(place => {
                          const pid = place.place_id || (place.id ? `db_${place.id}` : undefined);
                          const isSelected = pid ? !!selectedToilets[pid] : false;
                          
                          return (
                            <div 
                              key={place.place_id} 
                              style={toiletCardStyle(isSelected)}
                              onClick={() => toggleToiletSelection(place.place_id)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected ? '#d4edda' : '#e9ecef';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected ? '#e8f5e9' : '#f8f9fa';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{flex: 1}}>
                                <div style={{display: 'flex', alignItems: 'center', marginBottom: window.innerWidth <= 768 ? '0.2rem' : '0.25rem'}}>
                                  <span style={{fontSize: window.innerWidth <= 768 ? '0.85rem' : '0.9rem', fontWeight: 'bold', color: '#004C10'}}>
                                    {place.name}
                                  </span>
                                </div>
                                
                                                                <div style={{fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.8rem', color: '#666', marginBottom: window.innerWidth <= 768 ? '0.2rem' : '0.25rem'}}>
                                  {place.address || place.vicinity || place.areas?.address || 'Address not available'}
                                </div>
                                
                                {/* Opening status for all toilets */}
                                {(() => {
                                  // Database toilets with opening hours data
                                  if (place.place_id && place.place_id.startsWith('db_')) {
                                    if (openingHours[index] && openingHours[index][place.id]) {
                                      const { journeyDate, journeyTime } = location.state || {};
                                      const openStatus = checkIfToiletIsOpen(openingHours[index][place.id], journeyDate, journeyTime);
                                      
                                      return (
                                        <div style={{
                                          fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.8rem', 
                                          color: openStatus.isOpen ? '#28a745' : '#dc3545',
                                          marginBottom: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
                                          padding: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
                                          backgroundColor: openStatus.isOpen ? '#d4edda' : '#f8d7da',
                                          borderRadius: '6px',
                                          border: `1px solid ${openStatus.isOpen ? '#c3e6cb' : '#f5c6cb'}`
                                        }}>
                                          <span style={{fontWeight: 'bold'}}>
                                            {openStatus.isOpen ? '🟢 Open' : '🔴 Closed'}
                                          </span>
                                          <div style={{marginTop: window.innerWidth <= 768 ? '0.2rem' : '0.25rem', fontSize: window.innerWidth <= 768 ? '0.7rem' : '0.75rem'}}>
                                            {openStatus.reason}
                                            {openStatus.openTime && openStatus.closeTime && (
                                              <span> ({openStatus.openTime} - {openStatus.closeTime})</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div style={{
                                          fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.8rem', 
                                          color: '#6c757d', 
                                          marginBottom: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
                                          padding: window.innerWidth <= 768 ? '0.4rem' : '0.5rem',
                                          backgroundColor: 'transparent',
                                          fontStyle: 'italic'
                                        }}>
                                          Opening hours not available
                                        </div>
                                      );
                                    }
                                  }
                                  
                                  // Google Maps toilets - show as open (since Google Places only returns currently open establishments)
                                  else {
                                    return (
                                      <div style={{
                                        fontSize: '0.8rem', 
                                        color: '#28a745',
                                        marginBottom: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: '#d4edda',
                                        borderRadius: '6px',
                                        border: '1px solid #c3e6cb'
                                      }}>
                                        <span style={{fontWeight: 'bold'}}>
                                          🟢 Open
                                        </span>
                                        <div style={{marginTop: '0.25rem', fontSize: '0.75rem'}}>
                                          Currently open (Google Places)
                                        </div>
                                      </div>
                                    );
                                  }
                                })()}
                                
                                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem'}}>
                                  {/* Distance from route - subtle but included */}
                                  {place.distanceFromRoute && (
                                    <span style={{color: '#666'}}>
                                      📍 {place.distanceFromRoute}m from route
                                    </span>
                                  )}
                                  
                                  {/* Show all available features regardless of source */}
                                  {place.cleanliness_rating && (
                                    <span style={{color: place.cleanliness_rating >= 4 ? '#28a745' : place.cleanliness_rating >= 3 ? '#ffc107' : '#dc3545'}}>
                                      ⭐ {place.cleanliness_rating}/5
                                    </span>
                                  )}
                                  
                                  {place.is_accessible && (
                                    <span style={{color: '#007bff'}}>♿ Accessible</span>
                                  )}
                                  
                                  {place.is_free && (
                                    <span style={{color: '#28a745'}}>💰 Free</span>
                                  )}
                                  
                                  {/* Gender indicators with defensive programming */}
                                  {Boolean(place.is_men_only) && (
                                    <span style={{color: '#007bff'}}>🚹 Men</span>
                                  )}
                                  
                                  {Boolean(place.is_women_only) && (
                                    <span style={{color: '#e83e8c'}}>🚺 Women</span>
                                  )}
                                  
                                  {Boolean(place.is_gender_neutral) && (
                                    <span style={{color: '#6f42c1'}}>🚻 Gender neutral</span>
                                  )}
                                  
                                  {/* Show "Unspecified" for Google Places toilets or when gender is unknown */}
                                  {(!place.place_id || !place.place_id.startsWith('db_') || 
                                    (!Boolean(place.is_men_only) && !Boolean(place.is_women_only) && !Boolean(place.is_gender_neutral))) && (
                                    <span style={{color: '#6c757d'}}>❓ Gender unspecified</span>
                                  )}
                                  
                                  {place.rating && (
                                    <span style={{color: '#ffc107'}}>
                                      ⭐ {place.rating}/5 ({place.user_ratings_total || 0} reviews)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        </div>

                        {/* Loading State */}
                        {databaseLoading[index] && (
                          <div style={{fontSize: '0.9rem', color: '#666', fontStyle: 'italic', padding: '0.5rem', backgroundColor: '#fff3cd', borderRadius: '4px'}}>
                            🔄 Loading database loos...
                          </div>
                        )}
                    </div>
                  )}
                    </>
                  )}

                </div>
              );
            })}
          </div>
        ) : (
          <p>No route data available. Please go back and submit a route.</p>
        )}
        {error && <p style={{color: 'red'}}>{error}</p>}
      </div>
      <div ref={mapRef} style={mapContainerStyle} />
      
      {/* Loading overlay on map until markers are rendered */}
      {!markersRendered && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          textAlign: 'center',
          zIndex: 1000,
          pointerEvents: 'none',
        }}>
          <div style={{ fontSize: '1.1rem', color: '#004C10', marginBottom: '0.5rem' }}>
            🔄 Loading Map...
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>
            Please wait while we render your route
          </div>
        </div>
      )}
      
      {/* Mobile Overlay - Only visible when panel is collapsed */}
      {window.innerWidth <= 768 && collapsedRoutes.mobile && (
        <div style={{
          position: 'fixed',
          top: '4rem',
          left: '1rem',
          right: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '8px',
          padding: '0.75rem',
          zIndex: 5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          fontSize: '0.8rem',
          color: '#004C10',
          textAlign: 'center',
          backdropFilter: 'blur(4px)',
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
            📍 {startLocation} → {endLocation}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#666' }}>
            Tap ☰ to view routes and toilets
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteResults; 